import { Divider } from 'antd'
import { useMemo, useState } from 'react'

import ConfigIcon from '@/assets/icons/configure.svg?react'
import { Trans } from '@/components'
import { MachineType } from '@/consts/machine'
import { Instance } from '@/models/instance'
import { formatRamMB } from '@/utils/formatter.ts'

import { UpgradeHardwareModal } from './UpgradeHardwareModal'

interface InstanceHardwareProps {
  instance: Instance
  readOnly?: boolean
}

const convertGpuName = (val: any, hardware: any) => {
  if (hardware && hardware.maxRef > 1) {
    return `Shared ${val}`
  }
  return val
}
const convertMbToGb = (val: any) => Math.ceil(val / 1024)

const infoMap = {
  gpu: {
    // first is displayName, second is unit, third is convertFunc
    gpuName: ['GPU', undefined, convertGpuName],
    tflops: ['FLOPS', 'TFLOPS'],
    gpuRamMB: ['GPU RAM', 'GB', convertMbToGb],
  },
  machine: {
    cpuIds: ['CPU number', '', (v: any) => v.length || 0],
    ramMB: ['RAM', 'GB', convertMbToGb],
    // diskName: ['Disk Name'],
    diskGB: ['Disk size', 'GB'],
    cpuName: ['CPU'],
    // motherboardName: ['Motherboard'],
  },
}

export function InstanceHardware({ instance, readOnly = false }: InstanceHardwareProps) {
  const isSubscription = Boolean(instance.order)

  useMemo(() => {
    const result = []

    const { hardware } = instance as any
    const { gpu, machine, net } = infoMap as any

    if (instance.hostType === MachineType.GPU) {
      for (const key in gpu) {
        const convertFunc = gpu[key][2]
        const val = hardware.gpu[key]
        result.push({
          name: gpu[key][0],
          key,
          value: convertFunc ? convertFunc(val, hardware) : val,
          unit: gpu[key][1] || '',
        })
      }
    } else if (instance.hostType === MachineType.NPU) {
      result.push({
        name: 'NPU ram',
        key: 'npuRam',
        value: convertMbToGb(hardware.gpu.gpuRamMB),
        unit: '',
      })
    }

    for (const key in machine) {
      const convertFunc = machine[key][2]
      const val = hardware.machine[key]
      result.push({
        name: machine[key][0],
        key,
        value: convertFunc ? convertFunc(val) : val,
        unit: machine[key][1] || '',
      })
    }

    for (const key in net) {
      const convertFunc = net[key][2]
      const val = hardware.machine[key]
      result.push({
        name: net[key][0],
        key,
        value: convertFunc ? convertFunc(val) : val,
        unit: net[key][1] || '',
      })
    }

    return result
  }, [instance])

  const [openUpgradeHardwareModal, setOpenUpgradeHardwareModal] = useState(false)

  return (
    <div className="bg-panel-gray-100 rounded-lg t-xs text-tertiary pb-4">
      {instance.hardware.gpu.gpuIds.length > 0 && (
        <div className="flex flex-wrap pt-4 t-sm text-tertiary gap-y-4">
          <div className="w-1/4 px-4">
            <div className="text-secondary">
              <Trans i18nKey="instance.detail.hardware.gpu">GPU Model</Trans>
            </div>
            <div className="text-primary t-sb">{instance.hardware.gpu.gpuName}</div>
          </div>
          <div className="w-1/4 px-4">
            <div className="text-secondary">
              <Trans i18nKey="instance.detail.hardware.gpus">GPUs</Trans>
            </div>
            <div className="text-primary t-sb">{instance.hardware.gpu.gpuIds.length}</div>
          </div>
          <div className="w-1/4 px-4">
            <div className="text-secondary">
              <Trans i18nKey="instance.detail.hardware.gpuRam">GPU RAM</Trans>
            </div>
            <div className="text-primary t-sb">{formatRamMB(instance.hardware.gpu.gpuRamMB)}</div>
          </div>
          <div className="w-1/4 px-4"></div>
        </div>
      )}

      <div className="flex flex-wrap pt-4 t-xs text-tertiary gap-y-4">
        <div className="w-1/4 px-4">
          <div className="text-secondary">
            <Trans i18nKey="instance.detail.hardware.cpu">CPU Model</Trans>
          </div>
          <div className="text-primary t-sb">{instance.hardware.machine.cpuName}</div>
        </div>
        <div className="w-1/4 px-4">
          <div className="text-secondary">
            <Trans i18nKey="instance.detail.hardware.vCpus">vCPUs</Trans>
          </div>
          <div className="text-primary t-sb">{instance.hardware.machine.cpuIds.length}</div>
        </div>
        <div className="w-1/4 px-4">
          <div className="text-secondary">
            <Trans i18nKey="instance.detail.hardware.ram">RAM</Trans>
          </div>
          <div className="text-primary t-sb">{formatRamMB(instance.hardware.machine.ramMB)}</div>
        </div>
        <div className="w-1/4 px-4">
          <div className="text-secondary">
            <Trans i18nKey="instance.detail.hardware.storage">Storage</Trans>
          </div>
          <div className="text-primary t-sb">{instance.hardware.machine.diskGB} GB</div>
        </div>
      </div>
      {!readOnly && instance.isRunning && !isSubscription && (
        <div className="px-4 pb-4">
          <Divider className="mb-4" />
          <div className="flex gap-4 t-xs font-bold">
            <div
              className="text-btn-secondary cursor-pointer flex items-center justify-center gap-1"
              onClick={() => setOpenUpgradeHardwareModal(true)}
            >
              <ConfigIcon width={16} height={16} />
              <Trans i18nKey="instance.detail.hardware.upgradeHardwareBtn">Upgrade Hardware</Trans>
            </div>
          </div>
        </div>
      )}

      {openUpgradeHardwareModal ? (
        <UpgradeHardwareModal
          onClose={() => setOpenUpgradeHardwareModal(false)}
          instance={instance}
          open={openUpgradeHardwareModal}
        />
      ) : null}
    </div>
  )
}
